import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../api';
import { useTheme } from '../context/ThemeContext';
import { useSocket } from '../context/SocketContext';
import { useAlert } from '../hooks/useAlert';
import { useAuth } from '../context/AuthContext';
import { useGuestGate } from '../context/GuestGateContext';
import { SkeletonForm } from '../components/Skeleton';
import {
  PageHeader, SectionLabel, Chip, MoneyInput, SummaryRow, PayButton, PleaseWait, Dropdown, TextField,
} from '../components/Form';
import Modal from '../components/Modal';
import Button from '../components/Button';
import Icon from '../components/Icon';

interface PaymentMethod { id: string; name: string; gateway?: string | null; }
interface Game { id: string; name: string; credentialRequestsEnabled?: boolean; }
interface WithdrawLimits { min: number; max: number; }
interface WalletSummary { balance: number; }

type WithdrawMode = 'choice' | 'redemption' | 'gameBank' | 'wallet';

function allowsCredentialRequests(game: Game) {
  return game.credentialRequestsEnabled !== false;
}

export default function Withdraw() {
  const navigate = useNavigate();
  const location = useLocation();
  const { colors } = useTheme();
  const { orderUpdateCount } = useSocket();
  const { alert } = useAlert();
  const { t } = useTranslation();
  const { isGuest } = useAuth();
  const { requireAuth } = useGuestGate();

  const initialMode = (() => {
    const m = new URLSearchParams(location.search).get('mode');
    return m === 'wallet' || m === 'redemption' || m === 'gameBank' ? (m as WithdrawMode) : 'choice';
  })();

  const [mode, setMode] = useState<WithdrawMode>(initialMode);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethod | null>(null);
  const [games, setGames] = useState<Game[]>([]);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [limits, setLimits] = useState<WithdrawLimits>({ min: 40, max: 2000 });
  const [walletBalance, setWalletBalance] = useState(0);
  const [amount, setAmount] = useState('');
  const [tag, setTag] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showPleaseWait, setShowPleaseWait] = useState(false);
  const [showGamePicker, setShowGamePicker] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  // Live wallet balance — the reported bug was that this only fetched once on
  // mount and went stale. Refresh on mount, on order updates (socket), and when
  // the tab/window regains focus so the balance matches the backend.
  const refreshWallet = useCallback(async () => {
    if (isGuest) return; // no wallet to read while browsing
    try {
      const res = await api.get<WalletSummary>('/wallet/me');
      setWalletBalance(Number(res.data.balance || 0));
    } catch { /* best-effort */ }
  }, [isGuest]);

  useEffect(() => {
    (async () => {
      try {
        const [methodsRes, gamesRes, limitsRes, walletRes] = await Promise.allSettled([
          api.get<PaymentMethod[]>('/payment-methods'),
          api.get<Game[]>('/games'),
          api.get<WithdrawLimits>('/orders/withdraw/my-limits'),
          api.get<WalletSummary>('/wallet/me'),
        ]);
        if (methodsRes.status === 'fulfilled') {
          // Card/gateway methods are deposit-only — never offered for withdrawals.
          const payoutMethods = methodsRes.value.data.filter(m => !m.gateway);
          setPaymentMethods(payoutMethods);
          if (payoutMethods.length > 0) setSelectedMethod(payoutMethods[0]);
        }
        if (gamesRes.status === 'fulfilled') {
          setGames(gamesRes.value.data.filter(allowsCredentialRequests));
        }
        if (limitsRes.status === 'fulfilled') setLimits(limitsRes.value.data);
        if (walletRes.status === 'fulfilled') setWalletBalance(Number(walletRes.value.data.balance || 0));
        if (methodsRes.status === 'rejected' || gamesRes.status === 'rejected') {
          alert(t('withdraw.alerts.loadWarningTitle'), t('withdraw.alerts.loadWarningMessage'));
        }
      } catch {
        alert(t('withdraw.alerts.errorTitle'), t('withdraw.alerts.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [alert, t]);

  // Re-fetch wallet on order updates + when the tab regains focus/visibility.
  useEffect(() => {
    refreshWallet();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshWallet(); };
    const onFocus = () => refreshWallet();
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [refreshWallet, orderUpdateCount]);

  // Withdrawal limits are per-game: refetch with the selected game's id whenever
  // it changes so the displayed range matches what the server enforces at submit
  // (the deposit that sets the limit — and any happy-hour bonus on it — is scoped
  // to that game).
  useEffect(() => {
    if (!selectedGame) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<WithdrawLimits>('/orders/withdraw/my-limits', { params: { gameId: selectedGame.id } });
        if (!cancelled) setLimits(res.data);
      } catch { /* keep previously loaded limits */ }
    })();
    return () => { cancelled = true; };
  }, [selectedGame]);

  const resetForm = (nextMode: WithdrawMode) => {
    setAmount('');
    setTag('');
    if (nextMode === 'wallet') setSelectedGame(null);
    setMode(nextMode);
    setShowReviewModal(false);
  };

  const getValidatedAmount = () => {
    const parsed = Number(amount);
    if (!amount || Number.isNaN(parsed) || parsed <= 0) {
      alert(t('withdraw.alerts.invalidAmountTitle'), t('withdraw.alerts.invalidAmount'));
      return null;
    }
    if (!/^\d+$/.test(amount.trim())) {
      alert(t('withdraw.alerts.invalidAmountTitle'), t('withdraw.alerts.decimalsOnly'));
      return null;
    }
    const parsedCents = Math.round(parsed * 100);
    if (mode === 'redemption' || mode === 'gameBank') {
      if (!selectedGame) { alert(t('withdraw.alerts.missingInfoTitle'), t('withdraw.alerts.selectGameToWithdraw')); return null; }
      if (parsedCents < Math.round(limits.min * 100)) { alert(t('withdraw.alerts.amountTooLowTitle'), t('withdraw.alerts.minGameWithdrawal', { min: limits.min })); return null; }
      if (parsedCents > Math.round(limits.max * 100)) { alert(t('withdraw.alerts.amountTooHighTitle'), t('withdraw.alerts.maxGameWithdrawal', { max: limits.max })); return null; }
    }
    if (mode === 'gameBank' || mode === 'wallet') {
      if (!selectedMethod) { alert(t('withdraw.alerts.missingInfoTitle'), t('withdraw.alerts.selectReceivingMethod')); return null; }
      if (!tag.trim()) { alert(t('withdraw.alerts.missingInfoTitle'), t('withdraw.alerts.enterPayoutAccount')); return null; }
    }
    if (mode === 'wallet' && parsedCents > Math.round(walletBalance * 100)) {
      alert(t('withdraw.alerts.insufficientBalanceTitle'), t('withdraw.alerts.walletAvailable', { balance: walletBalance.toFixed(2) }));
      return null;
    }
    return parsed;
  };

  const handleReview = () => {
    if (getValidatedAmount() == null) return;
    setShowReviewModal(true);
  };

  const handleSubmit = async () => {
    const parsedAmount = getValidatedAmount();
    if (!parsedAmount) return;
    setShowReviewModal(false);
    setSubmitting(true);
    try {
      if (mode === 'redemption') {
        if (!selectedGame) return;
        await api.post('/orders/redemption/request', { gameId: selectedGame.id, amount: parsedAmount });
      } else if (mode === 'gameBank') {
        if (!selectedGame || !selectedMethod) return;
        await api.post('/orders/withdraw', {
          gameId: selectedGame.id,
          paymentMethodId: selectedMethod.id,
          payoutTag: tag.trim(),
          amount: parsedAmount,
        });
      } else {
        if (!selectedMethod) return;
        await api.post('/orders/withdraw/wallet', {
          paymentMethodId: selectedMethod.id,
          payoutTag: tag.trim(),
          amount: parsedAmount,
        });
        setWalletBalance(b => Math.max(0, Math.round(b * 100 - parsedAmount * 100) / 100));
      }
      setAmount('');
      setTag('');
      setShowPleaseWait(true);
      setTimeout(() => {
        setShowPleaseWait(false);
        navigate(mode === 'redemption' ? '/profile' : '/profile?openHistory=1');
      }, 2200);
    } catch (err: any) {
      const message = err?.response?.data?.message || t('withdraw.alerts.submitFailed');
      if (mode === 'wallet' && /below minimum|exceeds maximum/i.test(message)) {
        alert(t('withdraw.alerts.modeErrorTitle'), t('withdraw.alerts.modeErrorMessage'));
      } else {
        alert(t('withdraw.alerts.errorTitle'), message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isRedemption = mode === 'redemption';
  const isGameBank = mode === 'gameBank';
  const isGameSource = isRedemption || isGameBank;
  const reviewTitle = isRedemption ? t('withdraw.reviewTitle.redemption') : t('withdraw.reviewTitle.withdrawal');
  const submitLabel = isRedemption ? t('withdraw.submitLabel.redemption') : t('withdraw.submitLabel.withdrawal');
  const requestType = isRedemption ? t('withdraw.requestType.redemption') : isGameBank ? t('withdraw.requestType.gameBank') : t('withdraw.requestType.wallet');

  return (
    <div style={{ minHeight: '100dvh', backgroundColor: colors.bg }}>
      <PageHeader
        icon="arrow-circle-down"
        title={t('withdraw.title')}
        right={<Icon name="shield-alt" size={16} color={colors.accentDark} />}
      />

      {loading ? (
        <div style={{ padding: 20 }}><SkeletonForm fields={4} /></div>
      ) : (
        <div style={{ padding: 20, paddingBottom: 40 }}>
          {mode === 'choice' ? (
            <div data-tour="withdraw-choice">
              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 16, borderRadius: 14,
                backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
                marginBottom: 14,
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12,
                  backgroundColor: colors.accentSoft,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name="wallet" size={16} color={colors.accentText} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: colors.textSub }}>{t('withdraw.walletBalance')}</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: colors.text, marginTop: 2 }}>${walletBalance.toFixed(2)}</div>
                </div>
              </div>

              <PathCard
                icon="gamepad"
                title={t('withdraw.paths.redemption.title')}
                body={t('withdraw.paths.redemption.body')}
                onClick={() => resetForm('redemption')}
              />
              <PathCard
                icon="university"
                title={t('withdraw.paths.gameBank.title')}
                body={t('withdraw.paths.gameBank.body')}
                onClick={() => resetForm('gameBank')}
              />
              <PathCard
                icon="wallet"
                title={t('withdraw.paths.wallet.title')}
                body={t('withdraw.paths.wallet.body')}
                onClick={() => resetForm('wallet')}
              />
            </div>
          ) : (
            <>
              <button
                onClick={() => resetForm('choice')}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  color: colors.textSub, fontSize: 13, fontWeight: 800,
                  marginBottom: 4, minHeight: 44,
                }}
              >
                <Icon name="arrow-left" size={13} color={colors.textSub} />
                {t('withdraw.changeType')}
              </button>

              <div style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 16, borderRadius: 14,
                backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
              }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 14,
                  backgroundColor: isGameSource ? colors.infoSoft : colors.accentSoft,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Icon name={isGameSource ? 'gamepad' : 'wallet'} size={13} color={isGameSource ? colors.infoText : colors.accentText} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: colors.textSub, letterSpacing: 0.3 }}>
                    {isRedemption ? t('withdraw.modeLabel.redemption') : isGameBank ? t('withdraw.modeLabel.gameBank') : t('withdraw.modeLabel.wallet')}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: colors.text, marginTop: 2 }}>
                    {isGameSource ? t('withdraw.limitsRange', { min: limits.min, max: limits.max }) : t('withdraw.availableWallet', { balance: walletBalance.toFixed(2) })}
                  </div>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 4 }}>
                    {isGameSource ? t('withdraw.limitsNote.game') : t('withdraw.limitsNote.wallet')}
                  </div>
                </div>
              </div>

              {isGameSource ? (
                <>
                  <SectionLabel>{t('withdraw.steps.withdrawFromGame')}</SectionLabel>
                  <Dropdown icon="gamepad" text={selectedGame?.name || ''} placeholder={t('withdraw.selectAGame')} onClick={() => setShowGamePicker(true)} />

                  {isGameBank ? (
                    <>
                      <SectionLabel>{t('withdraw.steps.receivingMethodGame')}</SectionLabel>
                      <MethodGrid methods={paymentMethods} selected={selectedMethod} onSelect={setSelectedMethod} />

                      <SectionLabel>{t('withdraw.steps.accountGame', { method: selectedMethod?.name || t('withdraw.receivingDefault') })}</SectionLabel>
                      <TextField
                        icon="tag"
                        value={tag}
                        onChange={setTag}
                        placeholder={t('withdraw.accountPlaceholder', { method: selectedMethod?.name || t('withdraw.payoutDefault') })}
                        autoCapitalize="none"
                      />

                      <SectionLabel>{t('withdraw.steps.amountGameBank')}</SectionLabel>
                    </>
                  ) : (
                    <SectionLabel>{t('withdraw.steps.amountRedemption')}</SectionLabel>
                  )}
                  <MoneyInput value={amount} onChange={v => setAmount(v.replace(/[^0-9]/g, ''))} decimal={false} placeholder={t('withdraw.minAmount', { min: limits.min })} />
                </>
              ) : (
                <>
                  <SectionLabel>{t('withdraw.steps.receivingMethodWallet')}</SectionLabel>
                  <MethodGrid methods={paymentMethods} selected={selectedMethod} onSelect={setSelectedMethod} />

                  <SectionLabel>{t('withdraw.steps.accountWallet', { method: selectedMethod?.name || t('withdraw.receivingDefault') })}</SectionLabel>
                  <TextField
                    icon="tag"
                    value={tag}
                    onChange={setTag}
                    placeholder={t('withdraw.accountPlaceholder', { method: selectedMethod?.name || t('withdraw.payoutDefault') })}
                    autoCapitalize="none"
                  />

                  <SectionLabel>{t('withdraw.steps.amountWallet')}</SectionLabel>
                  <MoneyInput value={amount} onChange={v => setAmount(v.replace(/[^0-9]/g, ''))} decimal={false} placeholder={t('withdraw.maxAmount', { balance: walletBalance.toFixed(2) })} />

                  <div style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    backgroundColor: colors.warningSoft, border: `1px solid ${colors.warning}`,
                    borderRadius: 12, padding: 12, marginTop: 18,
                  }}>
                    <Icon name="exclamation-triangle" size={13} color={colors.warningText} style={{ marginTop: 2 }} />
                    <div style={{ flex: 1, fontSize: 12, lineHeight: 1.5, fontWeight: 600, color: colors.warningText }}>
                      {t('withdraw.walletDebitNote')}
                    </div>
                  </div>
                </>
              )}

              <PayButton label={reviewTitle} onClick={() => requireAuth(handleReview)} loading={submitting} />
            </>
          )}
        </div>
      )}

      <Modal open={showReviewModal} onClose={() => setShowReviewModal(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>{reviewTitle}</div>
        <div style={{
          backgroundColor: colors.bg, border: `1px solid ${colors.cardBorder}`,
          borderRadius: 14, padding: 14, marginBottom: 16,
        }}>
          <SummaryRow label={t('withdraw.review.type')} value={requestType} />
          {isGameSource && <SummaryRow label={t('withdraw.review.game')} value={selectedGame?.name || ''} />}
          {(isGameBank || mode === 'wallet') && (
            <>
              <SummaryRow label={t('withdraw.review.method')} value={selectedMethod?.name || ''} />
              <SummaryRow label={t('withdraw.review.account')} value={tag.trim()} />
            </>
          )}
          <SummaryRow label={t('withdraw.review.amount')} value={`$${Number(amount || 0).toFixed(2)}`} />
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Button label={t('actions.cancel')} variant="outline" onClick={() => setShowReviewModal(false)} />
          <Button label={submitLabel} loading={submitting} onClick={handleSubmit} />
        </div>
      </Modal>

      <Modal open={showGamePicker} onClose={() => setShowGamePicker(false)}>
        <div style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: colors.divider, margin: '0 auto 16px' }} />
        <div style={{ fontSize: 17, fontWeight: 700, color: colors.text, marginBottom: 12 }}>{t('withdraw.selectGame')}</div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {games.map(g => (
            <button
              key={g.id}
              onClick={() => { setSelectedGame(g); setShowGamePicker(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                padding: '13px 0', borderBottom: `1px solid ${colors.divider}`,
                background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left',
                minHeight: 50,
              }}
            >
              <Icon name="gamepad" size={14} color={selectedGame?.id === g.id ? '#14b8a6' : colors.textSub} />
              <div style={{
                flex: 1, fontSize: 15,
                color: selectedGame?.id === g.id ? '#14b8a6' : colors.text,
                fontWeight: selectedGame?.id === g.id ? 700 : 400,
              }}>
                {g.name}
              </div>
              {selectedGame?.id === g.id && <Icon name="check" size={13} color="#14b8a6" />}
            </button>
          ))}
        </div>
        <Button label={t('actions.cancel')} variant="outline" onClick={() => setShowGamePicker(false)} style={{ marginTop: 12 }} />
      </Modal>

      <PleaseWait
        open={showPleaseWait}
        title={t('withdraw.pleaseWait.title')}
        message={isRedemption ? t('withdraw.pleaseWait.redemption') : t('withdraw.pleaseWait.withdrawal')}
      />
    </div>
  );
}

function MethodGrid({
  methods, selected, onSelect,
}: {
  methods: PaymentMethod[];
  selected: PaymentMethod | null;
  onSelect: (m: PaymentMethod) => void;
}) {
  const { colors } = useTheme();
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {methods.map(m => (
        <Chip
          key={m.id}
          active={selected?.id === m.id}
          onClick={() => onSelect(m)}
          icon={<Icon name="wallet" size={13} color={selected?.id === m.id ? '#020617' : colors.textSub} />}
          label={m.name}
        />
      ))}
    </div>
  );
}

function PathCard({ icon, title, body, onClick }: { icon: string; title: string; body: string; onClick: () => void }) {
  const { colors } = useTheme();
  const { t } = useTranslation();
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 12,
        backgroundColor: colors.card, border: `1px solid ${colors.cardBorder}`,
        borderRadius: 14, padding: 14, marginBottom: 12, minHeight: 112,
        cursor: 'pointer', textAlign: 'left',
      }}
    >
      <div style={{
        width: 44, height: 44, borderRadius: 12,
        backgroundColor: colors.accentSoft,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon name={icon} size={18} color={colors.accentText} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 15, fontWeight: 900, color: colors.text }}>{title}</div>
        <div style={{ fontSize: 12, lineHeight: 1.4, color: colors.textSub, marginTop: 3 }}>{body}</div>
      </div>
      <div style={{
        backgroundColor: colors.accent, padding: '8px 12px', borderRadius: 999,
        color: '#020617', fontSize: 12, fontWeight: 900, minHeight: 36,
        display: 'flex', alignItems: 'center', flexShrink: 0,
      }}>
        {t('withdraw.choose')}
      </div>
    </button>
  );
}
